import { useEffect, useRef, useState } from 'react';

import { DialogWizardScreen } from './dialog-wizard.types';

interface UseDialogWizardStateOptions {
  /** Whether the async action is currently in progress (e.g. `view.state.isDeleting`). */
  isLoading: boolean;
  /** The error returned by the async action, or null/undefined if no error occurred. */
  error: Error | null | undefined;
  /** The function that triggers the async action (e.g. `view.deleteReport(...)`). */
  onAction: () => void;
  /** Called after the dialog is closed (resets internal state first). */
  onClose: () => void;
  /** The function that validates the form before triggering the action. */
  validate?: () => boolean;
}

interface UseDialogWizardStateResult {
  /** The current screen to render in the dialog wizard. */
  screen: DialogWizardScreen;
  /** Call this to trigger the action — sets screen to LOADING and invokes `onAction`. */
  handleAction: () => void;
  /** Call this to close the dialog — resets internal state and invokes `onClose`. */
  handleClose: () => void;
}

/**
 * Manages the screen state machine for dialog wizard components.
 *
 * Encapsulates the common pattern of:
 * 1. Starting on the CONFIRM screen
 * 2. Transitioning to LOADING when an action is triggered
 * 3. Transitioning to SUCCESS or ERROR based on async operation state
 * 4. Resetting to CONFIRM when the dialog is closed
 *
 * @example
 * ```tsx
 * const { screen, handleAction, handleClose } = useDialogWizardState({
 *   isLoading: view.state.isDeleting,
 *   error: view.state.errorDeleting,
 *   onAction: () => view.deleteReport({ reportId: report.id }),
 *   onClose
 * });
 *
 * return (
 *   <DeleteDialogWizard
 *     screen={screen}
 *     onDelete={handleAction}
 *     onClose={handleClose}
 *     ...
 *   />
 * );
 * ```
 */
export const useDialogWizardState = ({
  isLoading,
  error,
  onAction,
  onClose,
  validate
}: UseDialogWizardStateOptions): UseDialogWizardStateResult => {
  const hasSubmitted = useRef(false);
  const [screen, setScreen] = useState<DialogWizardScreen>(DialogWizardScreen.CONFIRM);

  useEffect(() => {
    if (!hasSubmitted.current) {
      return;
    }

    if (isLoading) {
      setScreen(DialogWizardScreen.LOADING);
    } else if (error) {
      setScreen(DialogWizardScreen.ERROR);
    } else {
      setScreen(DialogWizardScreen.SUCCESS);
    }
  }, [isLoading, error]);

  const handleAction = () => {
    if (validate && !validate()) {
      return;
    }
    hasSubmitted.current = true;
    onAction();
    setScreen(DialogWizardScreen.LOADING);
  };

  const handleClose = () => {
    hasSubmitted.current = false;
    setScreen(DialogWizardScreen.CONFIRM);
    onClose();
  };

  return { screen, handleAction, handleClose };
};
